import React, { useCallback, useEffect, useLayoutEffect, useMemo } from 'react';
import { SectionList, StyleSheet, View } from 'react-native';
import Animated from 'react-native-reanimated';
import { AppView, withAnimatedNavigationHeader, SCREEN_BOTTOM_PADDING, rem, Button, ButtonGroup, SettingsItemNavigate, CardView, } from 'design-system';
import Spacer from 'design-system/Spacer';
import { closedConnection, deleteConnection, getBankConnection, getSyncingStatus, setRetainedConnections, } from 'actions';
import { useAppDispatch, useAppSelector, useAppStore } from 'store/hooks';
import { selectActiveConnectionsWithoutManual, selectConnectionById, selectDeactivatedConnections, selectSavingsPlanConnectionId, } from 'reducers/selectors';
import StatusBar from 'design-system/StatusBar';
import { useMarginBottom } from 'hooks/useMarginBottom';
import useRefreshAccounts from 'hooks/useRefreshAccounts';
import useAppFrameDimensions from 'hooks/useAppFrameDimensions';
import SectionHeader from 'design-system/SectionHeader';
import ListLoader from 'design-system/ListLoader';
import Modal from 'utils/packages/Modal';
import NavigationHeaderRightIconButton from 'design-system/NavigationHeaderRightIconButton';
import { IconTrash } from 'design-system/icons';
import { IconSettings } from 'design-system/icons/IconSettings';
import Text from 'design-system/Text';
import { HELP_ARTICLES_KEY } from 'features/help/constants';
import { useOpenConnectionLogin } from 'hooks/useOpenConnectionLogin';
import { useHideAccount } from 'features/accounts/hooks/useHideAccount';
import { useCloseAccount } from 'features/accounts/hooks/useCloseAccount';
import { selectHasRentReporting, selectCurrentRentReportingState } from 'features/rent-reporting/selectors';
import { formatPlural, isUnlocked } from 'utils';
import IconBlock from 'design-system/icons/IconBlock';
import useLinkConnection from 'hooks/useLinkConnection';
import { UnavailableOnWebModal } from 'utils/packages/UnavailableOnWebModal';
import { selectConnectionsLimit } from 'features/premium/selectors';
import { selectUserTier } from 'features/premium/selectors/tier';
import { Tier } from 'features/premium/entities';
import { SET_UPGRADED_IN_ACTIVATE_FUNNEL } from 'actions/types';
import { getMySpaces } from 'features/spaces/actions';
import { BankConnectionAccount } from './components/BankConnectionAccount';
import BankConnectionInfoCard from './components/BankConnectionInfoCard';
import ConnectionUsedInAutosaveModal from './components/ConnectionUsedInAutosaveModal';
import RentReportingLinkedModal from './components/RentReportingLinkedModal';
import ManageBankConnectionModal from './components/ManageBankConnectionModal';
import { isWeb } from '../../../constants';
import { canRenewConnection, getRequiredTier } from './utils';
import AccountsTopSync from './AccountsTopSync';
const keyExtractor = (item, index) => (item.id === -1 ? index : item.id).toString();
const AnimatedSectionList = Animated.createAnimatedComponent(SectionList);
const BankConnectionScreenContent = ({ navigation, connection, onScroll }) => {
    const savingsPlanConnectionId = useAppSelector(selectSavingsPlanConnectionId);
    const syncingAccounts = useAppSelector((store) => store.linkBank.syncingAccounts?.[connection.id]);
    const hasRentReporting = useAppSelector(selectHasRentReporting);
    const currentRentReporting = useAppSelector(selectCurrentRentReportingState);
    const rentReporting = useMemo(() => {
        if (!hasRentReporting || !currentRentReporting)
            return undefined;
        return connection.allAccounts.some((account) => account.id === currentRentReporting.accountId)
            ? currentRentReporting
            : undefined;
    }, [connection.allAccounts, currentRentReporting, hasRentReporting]);
    const dispatch = useAppDispatch();
    const refreshAccounts = useRefreshAccounts();
    const store = useAppStore();
    const navigateToAutosave = useCallback(() => navigation.navigate('AutoSave'), []);
    const showWebBlock = useCallback(() => {
        UnavailableOnWebModal.show(`emma://bank-connections/${connection.id}`, 'Manage bank connection');
    }, [connection.id]);
    const showDeleteConfirmation = useCallback(() => {
        Modal.show(<>
        <Spacer.H16 />
        <Text TextThin-14 Secondary centered>
          You will lose all your historical data. If you are having issues with this connection, try to renew connection
          or browse our help guides.
        </Text>
        <Spacer.H16 />
        <ButtonGroup margins={false}>
          <Button brandBorder title="Get help" onPress={() => {
                Modal.hide(() => navigation.navigate('Help', { articlesKey: HELP_ARTICLES_KEY.BANK_CONNECT }, { pop: true }));
            }}/>
          <Button brandReversed title="Yes, let me delete" onPress={async () => {
                Modal.hide();
                const res = await dispatch(deleteConnection(connection.id));
                if (res?.type !== 'DELETE_CONNECTION_SUCCESS')
                    return;
                dispatch(getMySpaces());
                refreshAccounts(connection.allAccounts.map((account) => account.type));
                if (navigation.canGoBack()) {
                    navigation.pop();
                }
                else {
                    navigation.replace('Logins');
                }
            }}/>
          <Button brand title="No, thanks" onPress={() => Modal.hide()}/>
        </ButtonGroup>
      </>, {
            title: 'Are you sure?',
        });
    }, [connection.allAccounts, connection.id, refreshAccounts]);
    const onDeleteConnection = useCallback(() => {
        if (savingsPlanConnectionId === connection.id) {
            if (isWeb) {
                showWebBlock();
                return;
            }
            ConnectionUsedInAutosaveModal.show(navigateToAutosave, 'delete');
            return;
        }
        if (rentReporting) {
            RentReportingLinkedModal.show('connection', 'delete', showDeleteConfirmation);
            return;
        }
        showDeleteConfirmation();
    }, [connection.id, navigateToAutosave, rentReporting, savingsPlanConnectionId, showDeleteConfirmation, showWebBlock]);
    const { renewConnection } = useLinkConnection();
    const showCloseConfirmation = useCallback(() => {
        Modal.showConfirmation({
            title: 'Are you sure?',
            subtitle: 'This connection will stop syncing. It will be moved to the Closed section but data will still be visible across the app.',
            confirmButtonTitle: 'Yes, let me close',
            cancelButtonTitle: 'No, thanks',
            onConfirm: () => dispatch(closedConnection(connection)),
        });
    }, [connection]);
    const toggleConnectionClosed = useCallback(() => {
        if (connection.isClosed) {
            if (!canRenewConnection(connection)) {
                Modal.showConfirmation({
                    title: `Reopen ${connection.bankInfo.name}`,
                    subtitle: 'The connection will start syncing again, and your accounts will show up in your main accounts list',
                    cancelButtonTitle: 'Reopen connection',
                    confirmButtonTitle: 'Cancel',
                    onPressCancel: () => dispatch(closedConnection(connection)),
                });
                return;
            }
            if (isWeb) {
                showWebBlock();
                return;
            }
            Modal.showConfirmation({
                title: `Reconnect ${connection.bankInfo.name}`,
                subtitle: `To reopen this, you’ll need to renew the connection.\n\nOnce redirected, please select all the accounts you’d like to see in Emma.`,
                cancelButtonTitle: 'Continue',
                confirmButtonTitle: 'Cancel',
                onPressCancel: () => renewConnection(connection.bankInfo, connection.id),
            });
            return;
        }
        if (savingsPlanConnectionId === connection.id) {
            if (isWeb) {
                showWebBlock();
                return;
            }
            ConnectionUsedInAutosaveModal.show(navigateToAutosave, 'close');
            return;
        }
        if (rentReporting) {
            if (isWeb) {
                showWebBlock();
                return;
            }
            RentReportingLinkedModal.show('connection', 'close', showCloseConfirmation);
            return;
        }
        showCloseConfirmation();
    }, [
        connection,
        navigateToAutosave,
        renewConnection,
        rentReporting,
        savingsPlanConnectionId,
        showCloseConfirmation,
        showWebBlock,
    ]);
    const onSettingsPress = useCallback(() => {
        ManageBankConnectionModal.show({
            isClosed: connection.isClosed,
            toggleConnectionClosed,
            onDelete: onDeleteConnection,
        });
    }, [connection.isClosed, toggleConnectionClosed, onDeleteConnection]);
    useLayoutEffect(() => {
        navigation.setOptions({
            headerRight: () => {
                if (connection.deactivatedOverQuota) {
                    return (<NavigationHeaderRightIconButton onPress={onDeleteConnection}>
              <IconTrash colorKey="primary"/>
            </NavigationHeaderRightIconButton>);
                }
                return (<View style={styles.headerRight}>
            {!connection.isClosed && <AccountsTopSync connection={connection}/>}
            <NavigationHeaderRightIconButton onPress={onSettingsPress}>
              <IconSettings colorKey="primary"/>
            </NavigationHeaderRightIconButton>
          </View>);
            },
        });
    }, [connection, connection.deactivatedOverQuota, onDeleteConnection, onSettingsPress]);
    const onActivate = useCallback(() => {
        if (isWeb) {
            showWebBlock();
            return;
        }
        const state = store.getState();
        const deactivatedConnections = selectDeactivatedConnections(state);
        if (deactivatedConnections.length === 1) {
            const connectionsLimitMap = selectConnectionsLimit(state);
            const activeConnections = selectActiveConnectionsWithoutManual(state);
            const userTier = selectUserTier(state);
            const requiredTier = getRequiredTier(connectionsLimitMap, activeConnections.length + 1);
            const needsUpgrade = requiredTier !== Tier.free && !isUnlocked(userTier, requiredTier);
            const saveRetained = async () => {
                await dispatch(setRetainedConnections([...activeConnections.map(({ id }) => id), connection.id]));
                await dispatch(getBankConnection(connection.id));
                setTimeout(() => dispatch(getSyncingStatus()), 1000);
            };
            if (needsUpgrade) {
                navigation.navigate('EmmaProModal', {
                    tier: requiredTier,
                    isTierRequired: true,
                    onUpgradeCallback: () => {
                        saveRetained();
                        dispatch({
                            type: SET_UPGRADED_IN_ACTIVATE_FUNNEL,
                            payload: true,
                        });
                    },
                });
            }
            else {
                saveRetained();
            }
            return;
        }
        navigation.navigate('LoginsUpgrade', { fromConnectionId: connection.id });
    }, [connection.id, showWebBlock]);
    const login = useOpenConnectionLogin(navigation);
    const openBankLogin = useCallback(() => {
        login({ type: 'connection', data: connection }, false);
    }, [connection, login]);
    const ListHeaderComponent = useMemo(() => (<BankConnectionInfoCard data={connection} onActivate={onActivate} onReopen={toggleConnectionClosed} onRenew={openBankLogin} savingsPlanConnectionId={savingsPlanConnectionId} rentReporting={rentReporting}/>), [connection, onActivate, openBankLogin, rentReporting, savingsPlanConnectionId, toggleConnectionClosed]);
    const renderSectionHeader = useCallback(({ section }) => {
        if (!section.data.length || !section.title)
            return null;
        return (<SectionHeader horizontalMargins largeVerticalMargins toolTipTitle={section.toolTipTitle} toolTipSubTitle={section.toolTipSubTitle}>
        {section.title}
      </SectionHeader>);
    }, []);
    const ListFooterComponent = useMemo(() => {
        if (connection.isClosed || connection.deactivatedOverQuota)
            return null;
        const closedAccountsCount = (syncingAccounts || connection.allAccounts).filter((account) => account.isClosed).length;
        if (!closedAccountsCount)
            return null;
        return (<>
        <SectionHeader horizontalMargins largeVerticalMargins toolTipTitle="What are closed accounts?" toolTipSubTitle={`Closed accounts no longer sync, but their historical data remains visible across the app unless you hide them.\n\nYou can mark an account as closed once you’ve closed it with your bank, so you don’t lose your data.`}>
          Closed accounts
        </SectionHeader>
        <CardView noMarginTop noMarginBottom>
          <SettingsItemNavigate icon={<IconBlock colorKey="statusNegative"/>} title={`${closedAccountsCount} closed ${formatPlural('account', 'accounts', closedAccountsCount)}`} onPress={() => navigation.navigate('ClosedAccounts', { connectionId: connection.id })}/>
        </CardView>
      </>);
    }, [connection.allAccounts, connection.deactivatedOverQuota, connection.id, connection.isClosed, syncingAccounts]);
    const handleToggleClosed = useCloseAccount();
    const handleToggleHidden = useHideAccount();
    const renderItem = useCallback(({ item }) => (<BankConnectionAccount account={item} isConnectionClosed={connection.isClosed} isConnectionDeactivated={!!connection.deactivatedOverQuota} toggleClosed={() => handleToggleClosed(item)} toggleHidden={() => handleToggleHidden(item)}/>), [connection.isClosed, connection.deactivatedOverQuota, handleToggleClosed, handleToggleHidden]);
    const { paddingHorizontalStyle } = useAppFrameDimensions(isWeb);
    const contentContainerStyle = useMarginBottom('paddingBottom', SCREEN_BOTTOM_PADDING, paddingHorizontalStyle, styles.container);
    const sections = useMemo(() => {
        if (!connection.allAccounts.length && !syncingAccounts?.length)
            return [];
        if (connection.deactivatedOverQuota) {
            return [{ data: connection.allAccounts, title: 'Accounts' }];
        }
        const accounts = [];
        const hiddenAccounts = [];
        (syncingAccounts || connection.allAccounts).forEach((account) => {
            if (account.isClosed && !connection.isClosed)
                return;
            if (account.isHidden) {
                hiddenAccounts.push(account);
            }
            else {
                accounts.push(account);
            }
        });
        const result = [
            { data: accounts, title: 'Accounts' },
            {
                data: hiddenAccounts,
                title: 'Hidden accounts',
                toolTipTitle: 'What are hidden accounts?',
                toolTipSubTitle: `Hidden accounts continue to sync, but their balances and transactions aren’t visible in the app${connection.isClosed
                    ? '.\n\nFor closed bank connections, hidden accounts no longer sync and their balances and transactions aren’t visible in the app.'
                    : ''}`,
            },
        ];
        return result;
    }, [connection.allAccounts, connection.deactivatedOverQuota, connection.isClosed, syncingAccounts]);
    return (<AppView type="secondary" withFrame={false}>
      <StatusBar barStyle="dark-content" animated/>
      <AnimatedSectionList contentContainerStyle={contentContainerStyle} sections={sections} stickySectionHeadersEnabled={false} renderItem={renderItem} renderSectionHeader={renderSectionHeader} ItemSeparatorComponent={Spacer.H16} keyExtractor={keyExtractor} ListHeaderComponent={ListHeaderComponent} ListFooterComponent={ListFooterComponent} onScroll={onScroll}/>
    </AppView>);
};
const BankConnectionScreen = withAnimatedNavigationHeader(({ navigation, route, onScroll }) => {
    const { connectionId } = route.params;
    const dispatch = useAppDispatch();
    const store = useAppStore();
    const connection = useAppSelector((store) => selectConnectionById(store, connectionId));
    useLayoutEffect(() => {
        navigation.setOptions({
            title: connection?.bankInfo.name,
            headerTitle: connection?.bankInfo.name,
            ...(!connection && { headerRight: () => null }),
        });
    }, [connection, connection?.bankInfo.name]);
    useEffect(() => {
        dispatch(getBankConnection(connectionId)).then((res) => {
            const conn = selectConnectionById(store.getState(), connectionId);
            if (res?.type !== 'CONNECTION_SUCCESS' && !conn) {
                navigation.pop();
            }
        });
    }, [connectionId]);
    if (!connection)
        return <ListLoader />;
    return (<BankConnectionScreenContent navigation={navigation} route={route} connection={connection} onScroll={onScroll}/>);
}, ({ colors }) => ({
    hasCustomTitle: true,
    title: 'Bank connection',
    headerTintColor: colors.text.primary,
    headerColorOutputRange: [colors.background.dark, colors.cards.onDark],
}));
const styles = StyleSheet.create({
    container: {
        paddingTop: rem(8),
    },
    headerRight: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: rem(8),
    },
});
export default BankConnectionScreen;
